import React from 'react';
import { MousePointer2, X, Edit3, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * InspectModeOverlay - Shows selected element info and provides edit context
 */
const InspectModeOverlay = ({ 
  selectedElement, 
  onClearSelection, 
  onEditElement,
  isInspectMode,
  onToggleInspectMode
}) => {
  if (!isInspectMode && !selectedElement) return null;

  return (
    <div data-testid="inspect-mode-overlay">
      {/* Inspect Mode Toggle Banner */}
      {isInspectMode && !selectedElement && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50" data-testid="inspect-mode-banner">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-3">
            <MousePointer2 className="w-4 h-4 animate-pulse" />
            <span className="text-sm font-medium">Click any element to select it for editing</span>
            <button
              onClick={onToggleInspectMode}
              className="ml-2 p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Selected Element Info Panel */}
      {selectedElement && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-lg px-4" data-testid="selected-element-panel">
          <div className="bg-white rounded-xl shadow-2xl border border-zinc-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit3 className="w-4 h-4 text-white" />
                <span className="font-semibold text-white">Selected Element</span>
              </div>
              <button
                onClick={onClearSelection}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Element Type & Section */}
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                  {selectedElement.sectionType || 'Element'}
                </span>
                <span className="px-2 py-1 bg-zinc-100 text-zinc-600 text-xs font-medium rounded">
                  {selectedElement.elementType || 'text'}
                </span>
              </div>

              {/* Current Content Preview */}
              <div className="bg-zinc-50 rounded-lg p-3 mb-4">
                <div className="text-xs text-zinc-500 mb-1">Current Content:</div>
                <p className="text-sm text-zinc-800 line-clamp-2">
                  {selectedElement.currentText || selectedElement.currentContent || 'No text content'}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={() => onEditElement(selectedElement)}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  data-testid="edit-selected-element-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Edit with AI
                </Button>
                <Button
                  variant="outline"
                  onClick={onClearSelection}
                >
                  Cancel
                </Button>
              </div>

              {/* Hint */}
              <p className="text-xs text-zinc-500 mt-3 text-center">
                Describe your changes in the chat panel
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectModeOverlay;
